import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { inputClass } from "../../components/forms/Field";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  CARD,
  ErrorState,
  Notice,
  Panel,
} from "../../components/timetable/PanelKit";
import Skeleton from "../../components/Skeleton/Skeleton";
import AcademicTermsCard from "./AcademicTermsCard";
import { runErrorMessage } from "../../components/timetable/runErrors";
import {
  getTimetableSettings,
  updateTimetableSettings,
} from "../../services/schedulingService";
import { WEEKDAY_KEY } from "../../utils/helpers/timetableDisplay";

// Mirrors core.constants.MAX_LESSON_PERIOD — the backend rejects a longer day.
// Deliberately NOT the observation scheduler's PERIOD_CHOICES/MAX_PERIOD: that
// constant describes when an observation may be booked, this one describes how
// long a school day is, and they are free to diverge.
const MAX_PERIODS_PER_DAY = 8;

// There is deliberately no solver-budget field on this page any more. It was
// the same unanswerable "how many seconds?" question as the Generate form, just
// one tab over and held by the same user — and its bounds (1..3600) disagreed
// with the ones Generate accepted (5..600) while PRE-FILLING that field, so a
// school that saved 900 here got a guaranteed 400 on every run. The budget is
// now derived from the school's own curriculum; the deployment ceiling lives in
// settings.TIMETABLE_SOLVER.

// weekday follows JS Date.getDay(): 0 = Sunday … 6 = Saturday. Same convention
// as the model, PeriodPicker and the whole scheduling surface — never remap.
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

// A sensible first row for a school that has never opened this page.
const DEFAULT_PERIOD = { start: "08:30", end: "09:15" };

// <input type="time"> can hand back "HH:MM:SS"; the API wants "HH:MM".
const hhmm = (value) => String(value || "").slice(0, 5);


// Periods are positional: row index + 1 IS the period number, and the backend
// rejects anything else. Renumbering after every edit makes contiguity
// structural rather than something the operator has to get right.
const renumber = (rows) => rows.map((row, i) => ({ ...row, period: i + 1 }));

export default function SettingsPage() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [weekdays, setWeekdays] = useState([]);
  const [periods, setPeriods] = useState([]);
  // Serialized snapshot of the last saved state — drives the dirty check.
  const [baseline, setBaseline] = useState("");

  const applySettings = useCallback((data) => {
    const rows = renumber(
      (data?.periods || []).map((p) => ({
        start: hhmm(p.start),
        end: hhmm(p.end),
      })),
    );
    const next = {
      weekdays: [...(data?.teaching_weekdays || [])].sort((a, b) => a - b),
      periods: rows,
    };
    setWeekdays(next.weekdays);
    setPeriods(next.periods);
    setBaseline(JSON.stringify(next));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(false);
    getTimetableSettings({ signal: controller.signal })
      .then((data) => applySettings(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setLoadError(true);
      })
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [applySettings, reloadKey]);

  const current = useMemo(
    () =>
      JSON.stringify({
        weekdays,
        periods,
      }),
    [weekdays, periods],
  );
  const isDirty = baseline !== "" && current !== baseline;

  // -- validation ------------------------------------------------------------
  // Mirrors TimetableSettingsSerializer so the operator sees the problem before
  // the round-trip; the server stays the authority.
  const { errors, warnings } = useMemo(() => {
    const errs = [];
    const warns = [];

    if (weekdays.length === 0) errs.push(t("timetable.settings.errNoWeekday"));
    if (periods.length === 0) errs.push(t("timetable.settings.errNoPeriods"));
    if (periods.length > MAX_PERIODS_PER_DAY) {
      errs.push(
        t("timetable.settings.errTooManyPeriods", { max: MAX_PERIODS_PER_DAY }),
      );
    }

    periods.forEach((row, i) => {
      const n = i + 1;
      if (!row.start || !row.end) {
        errs.push(t("timetable.settings.errPeriodMissing", { n }));
        return;
      }
      if (row.start >= row.end) {
        errs.push(t("timetable.settings.errPeriodTimes", { n }));
        return;
      }
      // Overlapping bells are legal as far as the solver cares (it schedules by
      // period index, not clock time) but they are almost always a typo.
      const prev = periods[i - 1];
      if (prev?.end && row.start < prev.end) {
        warns.push(t("timetable.settings.warnOverlap", { n, prev: n - 1 }));
      }
    });

    return { errors: errs, warnings: warns };
  }, [weekdays, periods, t]);

  // -- mutations -------------------------------------------------------------

  const toggleWeekday = (day) => {
    setWeekdays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  const patchPeriod = (index, patch) => {
    setPeriods((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const addPeriod = () => {
    setPeriods((prev) => {
      if (prev.length >= MAX_PERIODS_PER_DAY) return prev;
      const last = prev[prev.length - 1];
      return renumber([...prev, last ? { start: last.end, end: "" } : { ...DEFAULT_PERIOD }]);
    });
  };

  const removePeriod = (index) => {
    setPeriods((prev) => renumber(prev.filter((_, i) => i !== index)));
  };

  const movePeriod = (index, delta) => {
    setPeriods((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return renumber(next);
    });
  };

  const save = async () => {
    if (errors.length > 0) return;
    setIsSaving(true);
    try {
      const data = await updateTimetableSettings({
        body: {
          teaching_weekdays: weekdays,
          periods: periods.map((row, i) => ({
            period: i + 1,
            start: hhmm(row.start),
            end: hhmm(row.end),
          })),
        },
      });
      applySettings(data);
      toast.success(t("timetable.settings.saved"));
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.settings.saveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  const capacity = weekdays.length * periods.length;

  // The page wrapper, the back link, the WIDTH and the permission guard all
  // live on TimetableWorkspace now — this is a tab panel, not a destination.
  //
  // It used to clamp itself to `max-w-3xl` with no `mx-auto`, so 768px of form
  // hugged the left edge of a centred 1152px column and every card stopped
  // ~384px short of the tab strip's own underline. It was also the only panel
  // with no PanelHeader at all: its largest type was a 14px section heading
  // where every sibling tab leads with 18px, so tabbing into it looked like
  // the page had failed to load its title. The header now reuses the tab's own
  // label and the intro paragraph that was already sitting loose above the
  // cards.
  return (
    <Panel>
      <PanelHeader
        title={t("timetable.tab.week")}
        subtitle={t("timetable.settings.intro")}
      />

      {isLoading ? (
        <SettingsSkeleton />
      ) : loadError ? (
        <ErrorState
          title={t("timetable.settings.loadFailed")}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : (
        <>
          {/* Two columns from `lg` up. The week and the bell schedule are the
              work; the generator defaults and the validation summary are
              reference, and at one column they pushed Save a screen further
              down while the bell-schedule rows — which need ~500px — stranded
              their move/delete controls at the far right of an empty row. */}
          <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="space-y-5">
              {/* -- teaching week ---------------------------------------- */}
              <section className={`${CARD} p-6`}>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("timetable.settings.weekTitle")}
                </h3>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  {t("timetable.settings.weekHint")}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {WEEKDAYS.map((day) => {
                    const checked = weekdays.includes(day);
                    return (
                      <label
                        key={day}
                        className={`cursor-pointer select-none rounded-xl border px-4 py-2 text-[11px] font-black uppercase tracking-widest transition focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 ${
                          checked
                            ? "border-indigo-600 bg-indigo-600 text-white shadow-md shadow-indigo-100"
                            : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:bg-indigo-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          disabled={isSaving}
                          onChange={() => toggleWeekday(day)}
                        />
                        {t(`timetable.weekday.${WEEKDAY_KEY[day]}`)}
                      </label>
                    );
                  })}
                </div>
              </section>

              {/* -- bell schedule ---------------------------------------- */}
              <section className={`${CARD} p-6`}>
                {/* The "N days × N periods = N slots" caption that used to sit
                    here is now the summary card in the side column. It was a
                    10px uppercase line tucked against the right edge of this
                    header — the least prominent thing on the page, describing
                    the constraint that governs the whole feature. */}
                <h3 className="text-sm font-bold text-slate-900">
                  {t("timetable.settings.periodsTitle")}
                </h3>
                <p className="mt-1 text-xs font-medium leading-relaxed text-slate-500">
                  {t("timetable.settings.periodsHint")}
                </p>

                <ul className="mt-4 space-y-2">
                  {periods.map((row, i) => (
                    <li
                      key={i}
                      className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2"
                    >
                      <span className="w-20 shrink-0 text-[11px] font-black uppercase tracking-widest text-slate-500">
                        {t("timetable.settings.periodN", { n: i + 1 })}
                      </span>
                      {/* `inputClass` is w-full, so the row width lives on the
                          wrapper — appending a w-* utility here would lose to it. */}
                      <div className="w-32">
                        <input
                          type="time"
                          value={row.start || ""}
                          disabled={isSaving}
                          aria-label={t("timetable.settings.startAria", { n: i + 1 })}
                          onChange={(e) => patchPeriod(i, { start: e.target.value })}
                          className={inputClass(
                            !row.start || Boolean(row.end && row.start >= row.end),
                          )}
                        />
                      </div>
                      <span className="text-slate-300" aria-hidden="true">–</span>
                      <div className="w-32">
                        <input
                          type="time"
                          value={row.end || ""}
                          disabled={isSaving}
                          aria-label={t("timetable.settings.endAria", { n: i + 1 })}
                          onChange={(e) => patchPeriod(i, { end: e.target.value })}
                          className={inputClass(
                            !row.end || Boolean(row.start && row.start >= row.end),
                          )}
                        />
                      </div>
                      {/* Not `ml-auto`. These three act on the two inputs beside
                          them, and pushing them to the far edge of the row put
                          ~130px of nothing between the control and its target —
                          which only got worse the wider the card became. */}
                      <div className="flex items-center gap-1">
                        <IconButton
                          label={t("timetable.settings.moveUp", { n: i + 1 })}
                          disabled={isSaving || i === 0}
                          onClick={() => movePeriod(i, -1)}
                          path="M5 15l7-7 7 7"
                        />
                        <IconButton
                          label={t("timetable.settings.moveDown", { n: i + 1 })}
                          disabled={isSaving || i === periods.length - 1}
                          onClick={() => movePeriod(i, 1)}
                          path="M19 9l-7 7-7-7"
                        />
                        <IconButton
                          label={t("timetable.settings.removePeriod", { n: i + 1 })}
                          disabled={isSaving}
                          danger
                          onClick={() => removePeriod(i)}
                          path="M6 18L18 6M6 6l12 12"
                        />
                      </div>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={addPeriod}
                  disabled={isSaving || periods.length >= MAX_PERIODS_PER_DAY}
                  className="mt-3 w-full rounded-xl border-2 border-dashed border-slate-200 py-2.5 text-xs font-bold text-slate-500 transition hover:border-indigo-300 hover:bg-indigo-50/50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  {t("timetable.settings.addPeriod")}
                </button>
              </section>
            </div>

            {/* Reference column: what the week ADDS UP TO, and whatever is
                currently wrong with it.

                This column has now outlived two occupants. It was headed
                "Generator defaults" over an "Only give teachers their own
                subjects" checkbox that the backend read nowhere and that could
                never have worked (the solver does not assign teachers — a
                manager does, per lesson, on the Lessons tab). Then it was a
                Notes textarea, which promised nothing false but was written and
                read by nothing except itself: no export, no Generate tab, no
                commit dialog surfaced it, so a handover note left here reached
                nobody. Both are gone from the API too.

                What is left is the one number on this page that constrains
                every other page: how many lesson slots the week actually holds.
                It was a faint uppercase caption inside the Bell schedule
                header; a class needing more periods than this is the single
                most common reason a generation run fails, so it is promoted to
                a persistent, always-populated summary — which is also what
                stops this column from being empty whenever nothing is wrong. */}
            <div className="space-y-5">
              {/* -- what the week adds up to ----------------------------- */}
              <section className={`${CARD} space-y-4 p-6`}>
                <h3 className="text-sm font-bold text-slate-900">
                  {t("timetable.settings.summaryTitle")}
                </h3>
                {/* Same value/label stack as the Generate tab's estimate strip,
                    deliberately: these are the same kind of fact, two tabs
                    apart, and a manager reads them minutes apart. */}
                <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
                  {[
                    { value: weekdays.length, label: t("timetable.settings.statDays") },
                    { value: periods.length, label: t("timetable.settings.statPeriods") },
                    { value: capacity, label: t("timetable.settings.statSlots") },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <dd className="text-xl font-black leading-none tabular-nums text-slate-900">
                        {stat.value}
                      </dd>
                      <dt className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {stat.label}
                      </dt>
                    </div>
                  ))}
                </dl>
              </section>

              {/* -- validation summary ----------------------------------- */}
              {errors.length > 0 && (
                <Notice tone="rose" role="alert" title={t("timetable.settings.problems")}>
                  <ul className="list-inside list-disc space-y-1">
                    {errors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </Notice>
              )}

              {warnings.length > 0 && (
                <Notice tone="amber" title={t("timetable.settings.checkThese")}>
                  <ul className="list-inside list-disc space-y-1">
                    {warnings.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </Notice>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              disabled={isSaving || !isDirty}
              className={BTN.quiet}
            >
              {t("timetable.settings.discard")}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={isSaving || !isDirty || errors.length > 0}
              className={BTN.primary}
            >
              {isSaving ? t("common.saving") : t("common.saveChanges")}
            </button>
          </div>
        </>
      )}

      {/* The school's TERM calendar — the year the week above repeats inside.
          It lives on this tab because this is already where a school says how
          its own calendar works, and because the write endpoint is gated on the
          same `organization.timetable.manage` the workspace guards; it needed no
          new page, route or permission.

          OUTSIDE the loading/error branch above on purpose: it is a separate
          request, and a school whose bell schedule failed to load must still be
          able to read and fix its terms. Below the Save row rather than above
          it, because that button saves the week and the bells and nothing here —
          each term saves itself, one row at a time. */}
      <div className="border-t border-slate-200 pt-5">
        <AcademicTermsCard />
      </div>
    </Panel>
  );
}

// Square icon button used by the bell-schedule row controls.
function IconButton({ label, path, onClick, disabled, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`p-1.5 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
        danger
          ? "text-rose-500 hover:bg-rose-50"
          : "text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
      }`}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d={path} />
      </svg>
    </button>
  );
}

// Layout-matched first-load placeholder — the same three cards in the same two
// columns as the real page, so the content does not jump sideways on load.
function SettingsSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <div className="space-y-5">
        <div className={`${CARD} p-6`}>
          <Skeleton className="mb-4 h-4 w-32" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-16 rounded-xl" />
            ))}
          </div>
        </div>
        <div className={`${CARD} p-6`}>
          <Skeleton className="mb-4 h-4 w-32" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>
      {/* Mirrors the summary card: a title over three value/label stacks. */}
      <div className={`${CARD} p-6`}>
        <Skeleton className="mb-4 h-4 w-40" />
        <div className="flex gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="h-5 w-8" />
              <Skeleton className="mt-1.5 h-2.5 w-12" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
